import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import LoginPage from "../components/auth/LoginPage";
import WorkflowGuideModal from "../components/help/WorkflowGuideModal";
import Preloader from "../components/layout/Preloader";
import Sidebar from "../components/layout/Sidebar";
import Topbar from "../components/layout/Topbar";
import AdminDashboard from "../AgentWorkspace";

import AppContent from "./AppContent";
import {
  ROUTE_PATHS,
  getDefaultPathForUser,
  getPathForLabel,
  getPathForTopNav,
  getTopNavForLabel,
  resolveRouteState,
} from "./navigation";

import { useCRMContext } from "../hooks/useCRMContext";
import { useLeads } from "../hooks/useLeads";
import { useLeadModals } from "../hooks/useLeadModals";
import { useNotifications } from "../hooks/useNotifications";
import { useReports } from "../hooks/useReports";

import { fetchJson } from "../lib/fetchJson";
import { NAV_ITEMS } from "../lib/constants";
import { buildAccessLookup, downloadViaBrowser, hasNamedAccess } from "../lib/utils";
import { CloseIcon, DocumentIcon } from "../lib/icons";

function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();

  const [currentUser, setCurrentUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [loggingIn, setLoggingIn] = useState(false);
  const [loginForm, setLoginForm] = useState({ username: "", password: "" });
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [lastImportAlert, setLastImportAlert] = useState(null);
  const [lastSyncAlert, setLastSyncAlert] = useState(null);
  const [exportJob, setExportJob] = useState(null);
  const [exportModal, setExportModal] = useState(null); // "ready" | "failed" | null
  const exportPollRef = useRef(null);
  const [pendingCreateLeadAgent, setPendingCreateLeadAgent] = useState(null);
  const [pendingCallLeadId, setPendingCallLeadId] = useState(null);
  const isAdminOpen = location.pathname === ROUTE_PATHS.support || location.pathname.startsWith(ROUTE_PATHS.support + '/');

  const roleScope = currentUser?.role_scope;
  const workspaceItems = roleScope?.workspace || [];
  const managementItems = roleScope?.management || [];
  const tabAccess = useMemo(
    () => buildAccessLookup(currentUser?.tab_access),
    [currentUser?.tab_access],
  );
  const featureAccess = useMemo(
    () => buildAccessLookup(currentUser?.feature_access),
    [currentUser?.feature_access],
  );
  const routeState = resolveRouteState(location.pathname, {
    workspaceItems,
    managementItems,
  });
  const activeTopNav = routeState.activeTopNav;
  const activeSidebarItem = routeState.activeSidebarItem;
  const canManageBulkImports = Boolean(roleScope?.can_bulk_upload);
  const canAccessSales =
    !currentUser ||
    tabAccess.size === 0 ||
    hasNamedAccess(tabAccess, "sales_crm", "sales crm");
  const canAccessSupport =
    Boolean(currentUser) &&
    hasNamedAccess(tabAccess, "support_crm", "support crm");
  const hasAgentWorkspaceAccess =
    canAccessSupport ||
    hasNamedAccess(featureAccess, "agent_workspace", "agent workspace");
  const availableNavItems = useMemo(() => {
    if (currentUser?.role === "dealer") {
      return [];
    }
    return NAV_ITEMS.filter(({ label }) => {
      if (label === "Sales") return canAccessSales;
      if (label === "Support") return canAccessSupport;
      return true;
    });
  }, [canAccessSales, canAccessSupport, currentUser?.role]);

  const {
    context,
    contextReady,
    integrationDiagnostics,
    loadContext,
    loadIntegrationDiagnostics,
    resetContext,
  } = useCRMContext();

  const modals = useLeadModals();
  const {
    bulkFeedback,
    bulkForm,
    bulkModal,
    bulkResult,
    closeBulkModal,
    documentFeedback,
    duplicateState,
    guideOpen,
    handleBulkFormChange,
    journeyModalOpen,
    journeyPickerOpen,
    leadForm,
    leadModal,
    openBulkReassign,
    openBulkUpload,
    resetModals,
    setBulkFeedback,
    setBulkForm,
    setBulkModal,
    setBulkResult,
    setDocumentFeedback,
    setDuplicateState,
    setGuideOpen,
    setJourneyModalOpen,
    setJourneyPickerOpen,
    setLeadForm,
    setLeadModal,
  } = modals;

  const reports = useReports({ currentUser, roleScope });
  const {
    activeReportTab,
    applyReportFilters,
    exportingReport,
    loadReports,
    loadingReports,
    reportData,
    reportDraftFilters,
    reportFilters,
    reportSyncKey,
    resetReports,
    setActiveReportTab,
    setExportingReport,
    setReportDraftFilters,
    setReportFilters,
    triggerReportSync,
    updateReportDraftFilter,
  } = reports;

  const leads = useLeads({
    activeSidebarItem,
    activeTopNav,
    bulkModalOpen: bulkModal.open,
    context,
    contextReady,
    currentUser,
    duplicateStateOpen: duplicateState.open,
    guideOpen,
    journeyModalOpen,
    journeyPickerOpen,
    leadModalOpen: leadModal.open,
    reportFilters,
    roleScope,
    setError,
  });
  const {
    agents,
    assignableAgents,
    bulkUploadingRef,
    clearLeadFilters,
    deferredSearch,
    dispositionPopupRef,
    filters,
    followUps,
    geographyData,
    handleLeadFilterChange,
    leadPageSize,
    leadResponse,
    loading,
    paginationItems,
    refreshSelectedLead,
    resetLeads,
    rootDispositions,
    search,
    selectedLead,
    selectedLeadId,
    setAssignableAgents,
    setFilters,
    setLeadPageSize,
    setLoading,
    setSearch,
    setSelectedLead,
    setSelectedLeadId,
    summary,
    loadWorkspace,
  } = leads;

  const {
    dismissNotification,
    notificationsOpen,
    setNotificationsOpen,
    unreadNotificationCount,
    visibleNotifications,
  } = useNotifications({
    canManageBulkImports,
    context,
    followUps,
    integrationDiagnostics,
    lastImportAlert,
    lastSyncAlert,
  });

  useEffect(() => {
    async function bootstrap() {
      try {
        setError("");
        const payload = await fetchJson("/api/accounts/auth/me/");
        const sessionUser = payload?.user || null;
        if (sessionUser) {
          setCurrentUser(sessionUser);
          await Promise.all([loadContext(), loadIntegrationDiagnostics()]);
        }
      } catch (requestError) {
        if (requestError.status !== 401) {
          setError(requestError.message);
        }
      } finally {
        setAuthLoading(false);
      }
    }

    bootstrap();
  }, []);

  // Poll export job status while pending/processing
  useEffect(() => {
    if (!exportJob || exportJob.status === "ready" || exportJob.status === "failed") {
      clearInterval(exportPollRef.current);
      return;
    }
    exportPollRef.current = setInterval(async () => {
      try {
        const jobs = await fetchJson("/api/crm/leads/full-export/");
        const latest = jobs?.[0];
        if (latest) setExportJob(latest);
      } catch (_) {}
    }, 5000);
    return () => clearInterval(exportPollRef.current);
  }, [exportJob?.status]);

  useEffect(() => {
    if (exportJob?.status === "ready" && exportJob?.job_id) {
      downloadViaBrowser(`/api/crm/leads/full-export/${exportJob.job_id}/download/?format=xlsx`);
      setExportModal("ready");
    } else if (exportJob?.status === "failed") {
      setExportModal("failed");
    }
  }, [exportJob?.status, exportJob?.job_id]);

  useEffect(() => {
    if (!currentUser || authLoading) {
      return;
    }

    if (!routeState.isValid) {
      navigate(getDefaultPathForUser(currentUser), { replace: true });
    }
  }, [authLoading, currentUser, navigate, routeState.isValid]);

  useEffect(() => {
    if (currentUser && tabAccess.size > 0 && !canAccessSales && canAccessSupport && !isAdminOpen) {
      navigate(ROUTE_PATHS.support, { replace: true });
    }
  }, [canAccessSales, canAccessSupport, currentUser, isAdminOpen, navigate, tabAccess.size]);

  useEffect(() => {
    if (activeTopNav === "Sales" && activeSidebarItem === "Dashboard") {
      const today = new Date().toISOString().slice(0, 10);
      setReportDraftFilters((prev) => ({ ...prev, dateFrom: today, dateTo: today }));
      setReportFilters((prev) => ({ ...prev, dateFrom: today, dateTo: today }));
    }
  }, [activeSidebarItem, activeTopNav, setReportDraftFilters, setReportFilters]);

  useEffect(() => {
    if (!currentUser || !contextReady) return;
    if (!(activeTopNav === "Sales" && ["Reports", "Dashboard"].includes(activeSidebarItem))) {
      return;
    }

    async function syncReports() {
      try {
        setError("");
        await loadReports(currentUser, { b2cOnly: true });
      } catch (requestError) {
        setError(requestError.message);
      }
    }

    syncReports();
    // NOTE: loadReports is intentionally NOT a dependency — it's recreated every
    // render, so including it re-fires this effect endlessly (infinite funnel requests).
  }, [
    activeSidebarItem,
    activeTopNav,
    contextReady,
    currentUser,
    reportFilters.assignedToId,
    reportFilters.dateFrom,
    reportFilters.dateTo,
    reportFilters.period,
    reportFilters.region,
    reportFilters.sourceId,
    reportFilters.status,
    reportFilters.teamId,
    reportSyncKey,
  ]);

  async function handleLoginSubmit(event) {
    event.preventDefault();
    try {
      setLoggingIn(true);
      setError("");
      setNotice("");
      const payload = await fetchJson("/api/accounts/auth/login/", {
        method: "POST",
        body: JSON.stringify(loginForm),
      });
      const user = payload.user;
      setCurrentUser(user);
      await Promise.all([loadContext(), loadIntegrationDiagnostics()]);
      navigate(getDefaultPathForUser(user), { replace: true });
      setLoginForm({ username: "", password: "" });
      setNotice(`Logged in as ${user.full_name}.`);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoggingIn(false);
    }
  }

  async function handleLogout() {
    try {
      setError("");
      await fetchJson("/api/accounts/auth/logout/", { method: "POST" });
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setCurrentUser(null);
      resetContext();
      resetLeads();
      resetModals();
      resetReports();
      setLastImportAlert(null);
      setLastSyncAlert(null);
      setPendingCreateLeadAgent(null);
      setPendingCallLeadId(null);
      setNotice("");
      setError("");
      navigate("/", { replace: true });
    }
  }

  async function handleAvailabilityChange(value) {
    try {
      setError("");
      setLoading((current) => ({ ...current, updatingAvailability: true }));
      const payload =
        value === "online"
          ? { availability_status: "online" }
          : { availability_status: "break", break_reason: value };
      const result = await fetchJson("/api/accounts/users/set-availability/", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      setCurrentUser((current) =>
        current
          ? {
              ...current,
              availability_status: result.availability_status,
              break_reason: result.break_reason || "",
            }
          : current,
      );
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading((current) => ({ ...current, updatingAvailability: false }));
    }
  }

  function handleLoginFieldChange(field, value) {
    setLoginForm((current) => ({ ...current, [field]: value }));
  }

  function navigateToLabel(label) {
    navigate(
      getPathForLabel(label, {
        workspaceItems,
        managementItems,
      }),
    );
  }

  function handleTopNavChange(label) {
    setNotificationsOpen(false);
    navigate(
      getPathForTopNav(label, {
        workspaceItems,
        managementItems,
      }),
    );
  }

  function handleSidebarClick(label) {
    setNotificationsOpen(false);

    if (label === "My Leads" || label === "Leads") {
      setFilters((current) => ({ ...current, page: 1, followUpBucket: "" }));
    }

    if (label === "Follow-ups") {
      setFilters((current) => ({ ...current, page: 1 }));
    }

    if (label === "Lead Journey") {
      setJourneyPickerOpen(true);
      return;
    }

    navigateToLabel(label);
  }

  function openAgentAdmin() {
    navigateToLabel("CRM Admin");
    setNotice("Use the Users tab to add or manage agents.");
  }

  if (authLoading) {
    return <Preloader visible={true} />;
  }

  if (!currentUser) {
    return (
      <LoginPage
        error={error}
        loggingIn={loggingIn}
        loginForm={loginForm}
        onChange={handleLoginFieldChange}
        onSubmit={handleLoginSubmit}
      />
    );
  }

  if (isAdminOpen) {
    return (
      <div className="crm-page">
        <AdminDashboard
          currentUser={currentUser}
          fetchJson={fetchJson}
          onLogout={handleLogout}
          onBack={() => navigate(getDefaultPathForUser(currentUser))}
        />
      </div>
    );
  }

  const leadsPageProps = {
    agents,
    assignableAgents,
    bulkUploadingRef,
    clearLeadFilters,
    deferredSearch,
    dispositionPopupRef,
    filters,
    followUps,
    geographyData,
    handleLeadFilterChange,
    leadPageSize,
    leadResponse,
    loading,
    paginationItems,
    refreshSelectedLead,
    rootDispositions,
    search,
    selectedLead,
    selectedLeadId,
    setAssignableAgents,
    setFilters,
    setLeadPageSize,
    setLoading,
    setSearch,
    setSelectedLead,
    setSelectedLeadId,
    loadWorkspace,
    bulkFeedback,
    bulkForm,
    bulkModal,
    bulkResult,
    closeBulkModal,
    documentFeedback,
    duplicateState,
    guideOpen,
    handleBulkFormChange,
    journeyModalOpen,
    journeyPickerOpen,
    leadForm,
    leadModal,
    openBulkReassign,
    openBulkUpload,
    setBulkFeedback,
    setBulkForm,
    setBulkModal,
    setBulkResult,
    setDocumentFeedback,
    setDuplicateState,
    setGuideOpen,
    setJourneyModalOpen,
    setJourneyPickerOpen,
    setLeadForm,
    setLeadModal,
    context,
    loadContext,
    loadIntegrationDiagnostics,
    integrationDiagnostics,
    loadReports,
    currentUser,
    roleScope,
    setError,
    setNotice,
    activeSidebarItem,
    activeTopNav,
    setLastImportAlert,
    setLastSyncAlert,
    pendingCreateLeadAgent,
    pendingCallLeadId,
    onPendingCreateLeadHandled: () => setPendingCreateLeadAgent(null),
    onPendingCallHandled: () => setPendingCallLeadId(null),
    onExportJobQueued: (job) => setExportJob(job),
  };

  return (
    <div className="crm-page">
      <Preloader visible={!contextReady} />
      <Topbar
        activeTopNav={activeTopNav}
        currentUser={currentUser}
        navItems={availableNavItems}
        notifications={visibleNotifications}
        notificationsOpen={notificationsOpen}
        onDismissNotification={dismissNotification}
        onNotificationAction={(notification) => {
          setNotificationsOpen(false);

          if (notification.category === "follow_up") {
            navigateToLabel("Follow-ups");
            return;
          }

          if (
            canManageBulkImports &&
            (notification.category === "import_issue" ||
              notification.category === "sync_issue")
          ) {
            navigateToLabel("My Leads");
            setBulkForm({ file: null, sourceId: "", importType: "bulk" });
            setBulkModal({ open: true, mode: "upload" });
          }

          if (notification.category === "phone_reveal") {
            navigateToLabel("My Agents");
          }
        }}
        onLogoClick={() => navigateToLabel("My Leads")}
        onOpenGuide={() => setGuideOpen(true)}
        onLogout={handleLogout}
        onNavChange={handleTopNavChange}
        onAvailabilityChange={handleAvailabilityChange}
        unreadNotificationCount={unreadNotificationCount}
        onToggleNotifications={() => setNotificationsOpen((current) => !current)}
        availabilitySaving={loading.updatingAvailability}
        roleScope={roleScope}
      />

      <div className="body-layout">
        <Sidebar
          activeSidebarItem={activeSidebarItem}
          currentUser={currentUser}
          managementItems={managementItems}
          onItemClick={handleSidebarClick}
          roleScope={roleScope}
          setIsAdmin={(val) => val ? navigate(ROUTE_PATHS.support) : navigate(getDefaultPathForUser(currentUser))}
          showAgentWorkspace={hasAgentWorkspaceAccess}
          summary={summary}
          workspaceItems={workspaceItems}
        />

        <main className="content-area">
          {error ? <p className="message-banner error">{error}</p> : null}
          {notice ? <p className="message-banner success">{notice}</p> : null}

          <AppContent
            activeReportTab={activeReportTab}
            activeSidebarItem={activeSidebarItem}
            activeTopNav={activeTopNav}
            agents={agents}
            applyReportFilters={applyReportFilters}
            context={context}
            currentUser={currentUser}
            exportingReport={exportingReport}
            followUps={followUps}
            geographyData={geographyData}
            integrationDiagnostics={integrationDiagnostics}
            leadsPageProps={leadsPageProps}
            loadContext={loadContext}
            loadIntegrationDiagnostics={loadIntegrationDiagnostics}
            loading={loading}
            loadingReports={loadingReports}
            loadWorkspace={loadWorkspace}
            openAgentAdmin={openAgentAdmin}
            openBulkReassign={openBulkReassign}
            openBulkUpload={openBulkUpload}
            refreshSelectedLead={refreshSelectedLead}
            reportData={reportData}
            reportDraftFilters={reportDraftFilters}
            reportFilters={reportFilters}
            roleScope={roleScope}
            selectedLead={selectedLead}
            selectedLeadId={selectedLeadId}
            setActiveReportTab={setActiveReportTab}
            setError={setError}
            setExportingReport={setExportingReport}
            setJourneyModalOpen={setJourneyModalOpen}
            setNotice={setNotice}
            setPendingCallLeadId={setPendingCallLeadId}
            setPendingCreateLeadAgent={setPendingCreateLeadAgent}
            setReportDraftFilters={setReportDraftFilters}
            setReportFilters={setReportFilters}
            setSelectedLead={setSelectedLead}
            setSelectedLeadId={setSelectedLeadId}
            triggerReportSync={triggerReportSync}
            updateReportDraftFilter={updateReportDraftFilter}
          />
        </main>
      </div>

      <WorkflowGuideModal
        currentUser={currentUser}
        onClose={() => setGuideOpen(false)}
        open={guideOpen}
      />

      {exportModal && (
        <div className="modal-backdrop" onClick={() => setExportModal(null)}>
          <div className="modal-panel compact" onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "flex-end", padding: "16px 16px 0" }}>
              <button type="button" className="close-button" onClick={() => setExportModal(null)}>
                <CloseIcon />
              </button>
            </div>
            <div className="export-modal-inner">
              <div className={`export-modal-icon ${exportModal}`}>
                <DocumentIcon />
              </div>
              <div>
                <p className={`export-modal-kicker ${exportModal}`}>
                  {exportModal === "ready" ? "Export Complete" : "Export Failed"}
                </p>
                <h2 className="export-modal-title">
                  {exportModal === "ready" ? "Your file is downloading" : "Export could not be generated"}
                </h2>
              </div>
              <p className="export-modal-body">
                {exportModal === "ready"
                  ? "Your full data export has started downloading automatically. Check your downloads folder."
                  : (exportJob?.error_message || "Something went wrong. Please try again from the Leads page.")}
              </p>
              <button
                type="button"
                className={`export-modal-btn ${exportModal}`}
                onClick={() => setExportModal(null)}
              >
                {exportModal === "ready" ? "Got it" : "Dismiss"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AppShell;
